import { useState } from "react";
import { CaretDownIcon, CheckIcon, PlusIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import {
  Popover as RadixPopover,
  PopoverContent as RadixPopoverContent,
  PopoverTrigger as RadixPopoverTrigger
} from "@libs/zapi-ui/shadcn";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TProject } from "@/zapi-frontend/stores/project";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";
import { useStartVersioning } from "../ProjectVersionBlock";
import { VersionHistoryPanel } from "../VersionHistoryPanel/VersionHistoryPanel";
import { useProjectVersionsStore } from "../../model/projectVersionsStore";

export const glassControl =
  "border border-white/40 bg-background/70 text-foreground shadow-xs backdrop-blur-md hover:bg-background/90";

const glassPill = cn("typo-label-sm h-7 max-w-full rounded-full px-2.5", glassControl);

type TProps = {
  project: TProject;
  hasEdit: boolean;
  organizationId: number;
  selectedVersionId: string;
  onSelectVersion: (versionId: string) => void;
  onListInvalidated: () => void;
};

export function VersionMenu({
  project,
  hasEdit,
  organizationId,
  selectedVersionId,
  onSelectVersion,
  onListInvalidated
}: TProps) {
  const versionGroupId = project.versionGroupId;
  const versions = project.versions ?? [];
  const isOpen = useProjectVersionsStore(
    (s) => versionGroupId != null && s.openPopoverVersionGroupId === versionGroupId
  );
  const setOpenPopoverVersionGroupId = useProjectVersionsStore((s) => s.setOpenPopoverVersionGroupId);

  if (versions.length === 0 || project.versionGroupNewest === false) return null;

  if (!versionGroupId) {
    if (!hasEdit) {
      return <span className={cn(glassPill, "flex items-center truncate")}>{versions[0]?.name}</span>;
    }
    return (
      <CreateVersionPopover
        projectId={project.id}
        organizationId={organizationId}
        onListInvalidated={onListInvalidated}
      />
    );
  }

  const active = versions.find((v) => v.id === selectedVersionId) ?? versions[0];

  return (
    <RadixPopover open={isOpen} onOpenChange={(open) => setOpenPopoverVersionGroupId(open ? versionGroupId : null)}>
      <RadixPopoverTrigger asChild>
        <Button variant="outline" size="xs" shape="pill" data-testid="version-trigger" className={glassPill}>
          <span className="min-w-0 truncate">{displayVersionName(active?.name)}</span>
          <CaretDownIcon data-icon="inline-end" className={cn("transition-transform", isOpen && "rotate-180")} />
        </Button>
      </RadixPopoverTrigger>
      <RadixPopoverContent
        forceMount
        align="start"
        collisionPadding={8}
        hideWhenDetached
        className="flex w-auto flex-col overflow-hidden border-0 p-0 shadow-none"
        style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
      >
        <VersionHistoryPanel
          selectedVersionId={selectedVersionId}
          onSelectVersion={onSelectVersion}
          rowProjectId={project.id}
          projectName={project.name}
          projectLogoUrl={project.logoUrl}
          projectIconShape={project.iconShape}
          projectIconColor={project.iconColor}
          versions={versions}
          hasEdit={hasEdit}
          organizationId={organizationId}
          onInvalidated={onListInvalidated}
        />
      </RadixPopoverContent>
    </RadixPopover>
  );
}

function CreateVersionPopover({
  projectId,
  organizationId,
  onListInvalidated
}: {
  projectId: string;
  organizationId: number;
  onListInvalidated: () => void;
}) {
  const { t } = useTranslation("projects");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const { startVersioning, isPending } = useStartVersioning({ projectId, organizationId, onListInvalidated });

  const submit = async () => {
    if (await startVersioning(name)) {
      setOpen(false);
      setName("");
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setName("");
      }}
    >
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="xs"
            shape="pill"
            data-testid={`project-${projectId}-create-version-button`}
            className={glassPill}
          />
        }
      >
        <PlusIcon data-icon="inline-start" weight="bold" />
        {t("card.createVersion")}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <PopoverTitle className="sr-only">{t("card.createVersion")}</PopoverTitle>
        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Input
            placeholder={t("card.versionNamePlaceholder")}
            data-testid={`project-${projectId}-create-version-input`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isPending}
          />
          <Button
            type="submit"
            size="icon"
            variant="secondary"
            aria-label={t("card.createVersion")}
            data-testid={`project-${projectId}-create-version-save-button`}
            disabled={isPending}
          >
            <CheckIcon weight="bold" />
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
